import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, DestroyRef, inject} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormControl } from '@angular/forms'
import { MessageService } from 'primeng/api'

import { ImageUploaderService } from '../../services/image-uploader.service'

interface UploadResponse {
  success: boolean
  data?: { link: string }
  errorText?: string
}

@Component({ selector: 'app-photo-uploader', templateUrl: './photo-uploader.component.html', styleUrls: ['./photo-uploader.component.scss'], standalone: false })
export class PhotoUploaderComponent implements OnInit, OnDestroy {
  private destroyRef = inject(DestroyRef)
  @Input() photoUrl: string | null = null
  @Input() placeholderIcon: { src: string; width?: string; height?: string }
  @Input() imageContainerStyle: Record<string, string>
  @Input() placeholderSelectedImage: { width: string; height?: string }
  @Output() photoUrlChange = new EventEmitter<string | null>()
  @Input() form: FormControl
  @Input() square: boolean = false
  @Input() btnMode = false
  @Input() label: string | null = null
  @Input() disabled: boolean = false
  @Input() uploadCategory: string | null = null
  constructor(
    private uploader: ImageUploaderService,
    private messageService: MessageService,
  ) {}
  request: ReturnType<ImageUploaderService['upload']> | undefined
  loading: boolean = false
  uniqueId: string = `photo_uploader_${Math.random().toString(36).substring(2, 11)}`

  @Output() loadingChange = new EventEmitter<boolean>()

  ngOnInit(): void {
    if (!this.placeholderIcon) {
      this.placeholderIcon = { src: 'assets/svg/user.svg', width: '25', height: '25' }
    }

    if (!this.placeholderSelectedImage) {
      this.placeholderSelectedImage = { width: '25', height: '25' }
    }

    if (this.form) {
      this.photoUrl = this.form.value as string | null
      this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
        this.photoUrl = value as string | null
      })
    }
  }

  onChange(e: Event) {
    const input = e.target as HTMLInputElement | null
    if (input?.files?.length) {
      this.request = this.uploader.upload(input.files[0], this.uploadCategory)
      this.loading = true
      this.loadingChange.emit(true)
      this.request.response
        .then((response) => {
          const res = response as UploadResponse
          if (res.success) {
            this.set(res.data!.link)
          } else {
            this.messageService.add({ severity: 'error', summary: 'File Upload Error', detail: res.errorText, life: 10000 })
          }
        })
        .finally(() => {
          this.loadingChange.emit(false)
          this.loading = false
        })
    }
  }

  ngOnDestroy(): void {
    if (this.request) {
      this.request.abort()
    }
  }

  set(link: string | null) {
    this.photoUrl = link
    this.photoUrlChange.emit(this.photoUrl)
    this.form.setValue(this.photoUrl)
  }

  reset(e: Event) {
    e.preventDefault()
    this.set(null)
  }
}
